import {
  BABIES_DEFAULT_AGE,
  BABIES_MAX_AGE,
  BABIES_MIN_AGE,
  CHILDREN_DEFAULT_AGE,
  CHILDREN_MAX_AGE,
  CHILDREN_MIN_AGE,
} from '@/domain/shared/constants'
import { useIntl } from '@/intl'
import { useRoomsParticipantsDistributionStore } from '@/scopes/common/components/Filters/Participants/useParticipantsStore'
import {
  Button,
  FormField,
  FormFieldControl,
  FormFieldLabel,
  SpaceBetween,
  tk,
  Typography,
} from '@sunwebgroup/ui-components'

import { sub } from 'date-fns'
import { partial } from 'ramda'

import * as React from 'react'
import { styled } from 'styled-components'
import DateDropdown from '../../DateDropdown'
import { NumberStepper } from './NumberStepper'

const Wrapper = styled(SpaceBetween)`
  width: 100%;
  padding-bottom: ${tk('spacing_lg')};
  border-bottom: 2px solid ${tk('color_semantic_border')};
  margin-bottom: ${tk('spacing_sm')};

  ${DateDropdown} {
    margin-top: 0 !important;
  }
`

const AgeRangeConfiguration = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`

const AgeRangeDescription = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const RoomTitle = styled(Typography)`
  display: inline-flex;
  align-items: center;
  text-transform: capitalize;
`

const AgeRangeDescriptionTitleLabel = styled(Typography)`
  color: ${tk('color_semantic_text_subdued')};
`

const DeleteRoomButton = styled(Button)`
  padding: ${tk('spacing_xs')};
`

const MAX_ADULTS = 19

const MAX_CHILDREN = 19
const MAX_BABIES = 19

export default function Room({ roomIndex }: { roomIndex: number }) {
  const intl = useIntl()

  const {
    setAdults,
    addChild,
    setChild,
    removeChild,
    addBaby,
    setBaby,
    removeBaby,
    deleteRoom,
    roomsParticipantDistribution,
  } = useRoomsParticipantsDistributionStore()

  const { adults, babies, children } = roomsParticipantDistribution[roomIndex]

  const isFirstRoom = roomIndex === 0

  const handleAdultsChange = partial(setAdults, [roomIndex])

  const handleSetChildrenDate = (index: number) => (date: Date) => {
    setChild(roomIndex, index, date)
  }

  const handleChildAddClick = () => addChild(roomIndex, sub(new Date(), { years: CHILDREN_DEFAULT_AGE, days: 1 }))

  const handleChildRemoveClick = () => removeChild(roomIndex)

  const handleSetBabiesDate = (index: number) => (date: Date) => {
    setBaby(roomIndex, index, date)
  }
  const handleBabyAddClick = () => addBaby(roomIndex, sub(new Date(), { years: BABIES_DEFAULT_AGE, days: 1 }))

  const handleBabyRemoveClick = () => removeBaby(roomIndex)

  const handleRoomDeleteClick = () => deleteRoom(roomIndex)

  return (
    <Wrapper spacing={tk('spacing_md')} direction="y" justifyContent="center">
      <SpaceBetween direction="x" justifyContent="space-between">
        <RoomTitle $variant="label">
          {intl.formatMessage({
            id: 'quicksearch_filter_participants_room_singular',
          })}
          {' '}
          {roomIndex + 1}
        </RoomTitle>
        {!isFirstRoom && (
          <DeleteRoomButton $hierarchy="tertiary" onClick={handleRoomDeleteClick}>
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_delete_room',
            })}
          </DeleteRoomButton>
        )}
      </SpaceBetween>
      <AgeRangeConfiguration data-qa={`adults-number-stepper-room-${roomIndex}`}>
        <AgeRangeDescription>
          <Typography $variant="label">
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_adults',
            })}
          </Typography>
          <AgeRangeDescriptionTitleLabel>
            {intl.formatMessage(
              { id: 'quicksearch_filter_participants_adults_description' },
              { min_age: CHILDREN_MAX_AGE },
            )}
          </AgeRangeDescriptionTitleLabel>
        </AgeRangeDescription>
        <NumberStepper value={adults} min={isFirstRoom ? 1 : 0} max={MAX_ADULTS} onValueChange={handleAdultsChange} />
      </AgeRangeConfiguration>
      <AgeRangeConfiguration>
        <AgeRangeDescription>
          <Typography $variant="label">
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_children',
            })}
          </Typography>
          <AgeRangeDescriptionTitleLabel>
            {intl.formatMessage(
              { id: 'quicksearch_filter_participants_minors_description' },
              { min_age: CHILDREN_MIN_AGE, max_age: CHILDREN_MAX_AGE - 1 },
            )}
          </AgeRangeDescriptionTitleLabel>
        </AgeRangeDescription>
        <NumberStepper
          value={children.length}
          max={MAX_CHILDREN}
          onIncrementClick={handleChildAddClick}
          onDecrementClick={handleChildRemoveClick}
        />
      </AgeRangeConfiguration>
      {children.map((date: Date, index: number) => (
        <FormField key={index}>
          <FormFieldLabel>
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_birthdate_child',
            })}
            {' '}
            {index + 1}
          </FormFieldLabel>
          <FormFieldControl>
            <DateDropdown
              value={date}
              onValueChange={handleSetChildrenDate(index)}
              min={sub(new Date(), { years: CHILDREN_MAX_AGE })}
              max={sub(new Date(), { years: CHILDREN_MIN_AGE, days: 1 })}
            />
          </FormFieldControl>
        </FormField>
      ))}
      <AgeRangeConfiguration>
        <AgeRangeDescription>
          <Typography $variant="label">
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_babies',
            })}
          </Typography>
          <AgeRangeDescriptionTitleLabel>
            {intl.formatMessage(
              { id: 'quicksearch_filter_participants_minors_description' },
              { min_age: BABIES_MIN_AGE, max_age: BABIES_MAX_AGE - 1 },
            )}
          </AgeRangeDescriptionTitleLabel>
        </AgeRangeDescription>
        <NumberStepper
          value={babies.length}
          max={MAX_BABIES}
          onIncrementClick={handleBabyAddClick}
          onDecrementClick={handleBabyRemoveClick}
        />
      </AgeRangeConfiguration>
      {babies.map((date: Date, index: number) => (
        <FormField key={index}>
          <FormFieldLabel>
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_birthdate_baby',
            })}
            {' '}
            {index + 1}
          </FormFieldLabel>
          <FormFieldControl>
            <DateDropdown
              key={index}
              value={date}
              min={sub(new Date(), { years: BABIES_MAX_AGE })}
              max={sub(new Date(), { years: BABIES_MIN_AGE, days: 1 })}
              onValueChange={handleSetBabiesDate(index)}
            />
          </FormFieldControl>
        </FormField>
      ))}
    </Wrapper>
  )
}
